/**
 * Edit dialogs for LCC records (Organization, Contact). Used from Lead Detail
 * tabs and the Organizations/Contacts views. PATCHes go to the demo Worker,
 * which mirrors the edit into the activity log.
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { lccApi, type LccContact, type LccOrganization } from '@/lib/lcc-api';
import { useLccInvalidate } from './lead-actions';

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] font-normal text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
    </label>
  );
}

export function EditOrganizationDialog({
  organization,
  open,
  onOpenChange,
}: {
  organization: LccOrganization;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const [form, setForm] = useState({
    name: organization.name,
    website: organization.website ?? '',
    industry: organization.industry ?? '',
    phone: organization.phone ?? '',
    email: organization.email ?? '',
    status: organization.status,
    notes: organization.notes ?? '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: organization.name,
        website: organization.website ?? '',
        industry: organization.industry ?? '',
        phone: organization.phone ?? '',
        email: organization.email ?? '',
        status: organization.status,
        notes: organization.notes ?? '',
      });
    }
  }, [open, organization]);

  const mutation = useMutation({
    mutationFn: () => lccApi.updateOrganization(organization.id, form),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  const set = (key: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit organization</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2.5 sm:grid-cols-2">
          <Field label="Name" value={form.name} onChange={set('name')} />
          <Field label="Industry" value={form.industry} onChange={set('industry')} />
          <Field label="Website" value={form.website} onChange={set('website')} placeholder="https://…" />
          <Field label="Status" value={form.status} onChange={set('status')} placeholder="active" />
          <Field label="Phone" value={form.phone} onChange={set('phone')} />
          <Field label="Email" value={form.email} onChange={set('email')} />
        </div>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] font-normal text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </label>
        {mutation.isError && (
          <p className="text-[12px] text-destructive">
            {mutation.error instanceof Error ? mutation.error.message : 'Update failed'}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            size="sm"
            disabled={!form.name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditContactDialog({
  contact,
  open,
  onOpenChange,
}: {
  contact: LccContact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const [form, setForm] = useState({
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    roleTitle: contact.roleTitle ?? '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        roleTitle: contact.roleTitle ?? '',
      });
    }
  }, [open, contact]);

  const mutation = useMutation({
    mutationFn: () => lccApi.updateContact(contact.id, form),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  const set = (key: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit contact</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2.5 sm:grid-cols-2">
          <Field label="First name" value={form.firstName} onChange={set('firstName')} />
          <Field label="Last name" value={form.lastName} onChange={set('lastName')} />
          <Field label="Email" value={form.email} onChange={set('email')} />
          <Field label="Phone" value={form.phone} onChange={set('phone')} />
          <Field label="Role / title" value={form.roleTitle} onChange={set('roleTitle')} />
        </div>
        {mutation.isError && (
          <p className="text-[12px] text-destructive">
            {mutation.error instanceof Error ? mutation.error.message : 'Update failed'}
          </p>
        )}
        <DialogFooter>
          <Button type="button" size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
